import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Navigate, Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";
import { ForgotPasswordPage } from "@/components/supabase/forgot-password-page";
import { SetPasswordPage } from "@/components/supabase/set-password-page";
import { OAuthConsentPage } from "@/components/supabase/oauth-consent-page";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import deals from "../deals";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { MobileWindowStateProvider } from "../layout/MobileWindowState";
import { SignupPage } from "../login/SignupPage";
import { ConfirmationRequired } from "../login/ConfirmationRequired";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import {
  getAuthProvider as defaultAuthProviderBuilder,
  getDataProvider as defaultDataProviderBuilder,
} from "../providers/supabase";
import sales from "../sales";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultCompanySectors,
  defaultCurrency,
  defaultDarkModeLogo,
  defaultDealCategories,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLightModeLogo,
  defaultNoteStatuses,
  defaultTaskTypes,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { StartPage } from "../login/StartPage.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { MobileTasksList } from "../tasks/MobileTasksList.tsx";
import { TaskList } from "../tasks/TaskList";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyListMobile } from "../companies/CompanyListMobile.tsx";
import { CompanyCreateMobile } from "../companies/CompanyCreateMobile.tsx";
import { CompanyEditMobile } from "../companies/CompanyEditMobile.tsx";
import { ContactCreateMobile } from "../contacts/ContactCreateMobile.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import teamTasks from "@/components/fretum/team-tasks";
import events from "@/components/fretum/calendar/events";
import { CalendarPage } from "@/components/fretum/calendar/CalendarPage";
import deliveryTemplates from "@/components/fretum/delivery/templates";
import styles from "@/components/fretum/styles";
import { StylePresent } from "@/components/fretum/styles/StylePresent";
import wikiPages from "@/components/fretum/wiki";
import timeEntries from "@/components/fretum/time";
import subscriptions from "@/components/fretum/finance/subscriptions";
import quotes, { QuotePrint } from "@/components/fretum/finance/quotes";
import invoices from "@/components/fretum/finance/invoices";
import { PublicSharePage } from "@/components/fretum/portal/PublicSharePage";
import { MarketIntelPage } from "@/components/market-intel/MarketIntelPage";
import { CompetitorsPage } from "@/components/competitors/CompetitorsPage";
import { MarketingPage } from "@/components/marketing/MarketingPage";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";
import {
  MOBILE_CALENDAR_PATH,
  MOBILE_CRM_SETTINGS_PATH,
} from "../layout/mobileModules";
import { MobileMorePage } from "../layout/MobileMorePage";
import { MobileAgenda } from "../mobile/MobileAgenda";
import { MobileDealShow, MobileReports } from "../mobile/MobileReports";
import { DeskDealReport, DeskReports } from "../mobile/DeskPages";
import { SplashHold } from "../splash/SplashGates";
import { useSplashStart } from "../splash/useSplash";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  dataProvider?: CrmDataProvider;
  authProvider?: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * This component sets up and renders the main CRM application using `ra-core`. It provides
 * default configurations and themes but allows for customization through props. The component
 * seeds the store with any custom prop values for backwards compatibility.
 *
 * @param {LabeledValue[]} companySectors - The list of company sectors used in the application.
 * @param {string} currency - The ISO 4217 currency code used to format monetary values (e.g. "USD", "EUR", "GBP").
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {LabeledValue[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} darkModeLogo - Logo shown in dark mode and on the auth pages. Must be an imported asset, an absolute URL, or a data URI — never a route-relative path like "./logos/x.svg", which breaks on nested routes such as /oauth/consent (issue #291).
 * @param {string} lightModeLogo - Logo shown in light mode. Same rule as darkModeLogo: imported asset, absolute URL, or data URI only.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {LabeledValue[]} taskTypes - The types of tasks used in the application.
 * @param {string} title - The title of the CRM application.
 *
 * @returns {JSX.Element} The rendered CRM application.
 *
 * @example
 * // Basic usage of the CRM component
 * import { CRM } from '@/components/atomic-crm/dashboard/CRM';
 *
 * const App = () => (
 *     <CRM
 *         darkModeLogo="https://example.com/logo-dark.svg"
 *         lightModeLogo="https://example.com/logo-light.svg"
 *         title="My Custom CRM"
 *         lightTheme={{
 *             ...defaultTheme,
 *             palette: {
 *                 primary: { main: '#0000ff' },
 *             },
 *         }}
 *     />
 * );
 *
 * export default App;
 */
export const CRM = ({
  companySectors = defaultCompanySectors,
  currency = defaultCurrency,
  dealCategories = defaultDealCategories,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  darkModeLogo = defaultDarkModeLogo,
  lightModeLogo = defaultLightModeLogo,
  noteStatuses = defaultNoteStatuses,
  taskTypes = defaultTaskTypes,
  title = defaultTitle,
  dataProvider = defaultDataProviderBuilder(),
  authProvider = defaultAuthProviderBuilder(),
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  disableTelemetry,
  ...rest
}: CRMProps) => {
  useEffect(() => {
    if (
      disableTelemetry ||
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      typeof window.location === "undefined" ||
      typeof Image === "undefined"
    ) {
      return;
    }
    const img = new Image();
    img.src = `https://atomic-crm-telemetry.marmelab.com/atomic-crm-telemetry?domain=${window.location.hostname}`;
  }, [disableTelemetry]);

  // Seed the store with CRM prop values if not already stored
  // (backwards compatibility for prop-based config)
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        companySectors,
        currency,
        dealCategories,
        dealPipelineStatuses,
        dealStages,
        noteStatuses,
        taskTypes,
        title,
        darkModeLogo,
        lightModeLogo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();
  useSplashStart();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      handleCallback: async (params: any) => {
        if (!authProvider.handleCallback) {
          throw new Error(
            "handleCallback is not implemented in the authProvider",
          );
        }
        const result = await authProvider.handleCallback(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <MobileWindowStateProvider>
      <ResponsiveAdmin
        dataProvider={dataProvider}
        authProvider={wrappedAuthProvider}
        i18nProvider={i18nProvider}
        store={store}
        loginPage={StartPage}
        loading={SplashHold}
        requireAuth
        disableTelemetry
        {...rest}
      />
    </MobileWindowStateProvider>
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes noLayout>
        <Route path={SignupPage.path} element={<SignupPage />} />
        <Route
          path={ConfirmationRequired.path}
          element={<ConfirmationRequired />}
        />
        <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
        <Route
          path={ForgotPasswordPage.path}
          element={<ForgotPasswordPage />}
        />
        <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
        <Route path={StylePresent.path} element={<StylePresent />} />
        <Route path={QuotePrint.path} element={<QuotePrint />} />
        <Route path={PublicSharePage.path} element={<PublicSharePage />} />
      </CustomRoutes>

      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        <Route path={ImportPage.path} element={<ImportPage />} />
        <Route path={ChangelogPage.path} element={<ChangelogPage />} />
        <Route path={CalendarPage.path} element={<CalendarPage />} />
        <Route path="/agenda" element={<MobileAgenda />} />
        <Route path="/informes" element={<DeskReports />} />
        <Route path="/informes/:id" element={<DeskDealReport />} />
        {/* Messages module removed: old links land on the home page. */}
        <Route path="/mensajes" element={<Navigate to="/" replace />} />
        <Route path={MarketIntelPage.path} element={<MarketIntelPage />} />
        <Route path={CompetitorsPage.path} element={<CompetitorsPage />} />
        <Route path={MarketingPage.path} element={<MarketingPage />} />
      </CustomRoutes>
      <Resource name="deals" {...deals} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="deal_notes" />
      <Resource name="tasks" list={TaskList} />
      <Resource name="sales" {...sales} />
      <Resource name="tags" />
      {/* Fretum hub */}
      <Resource name="team_tasks" {...teamTasks} />
      <Resource name="events" {...events} />
      <Resource name="team_task_comments" />
      <Resource name="time_entries" {...timeEntries} />
      <Resource name="calendar_items" />
      <Resource name="deal_milestones" />
      <Resource name="deal_milestones_progress" />
      <Resource name="deals_delivery" />
      <Resource name="deals_time" />
      <Resource name="project_links" />
      <Resource name="share_links" />
      <Resource name="delivery_templates" {...deliveryTemplates} />
      <Resource name="styles" {...styles} />
      <Resource name="wiki_pages" {...wikiPages} />
      <Resource name="subscriptions" {...subscriptions} />
      <Resource name="quotes" {...quotes} />
      <Resource name="invoices" {...invoices} />
      <Resource name="gdpr_checklists" />
      {/* Market intelligence (reports and digests are read-only) */}
      <Resource name="market_daily_reports" />
      <Resource name="market_daily_items" />
      <Resource name="market_weekly_digests" />
      <Resource name="market_competitors" />
      <Resource name="market_competitor_updates" />
      <Resource name="market_ideas" />
      <Resource name="market_opportunities" />
      {/* Competencia (competitor intelligence, trends, SWOT) */}
      <Resource name="competitor_social_profiles" />
      <Resource name="competitor_social_snapshots" />
      <Resource name="competitor_marketing_activity" />
      <Resource name="competitor_products" />
      <Resource name="market_trends" />
      <Resource name="market_swot_analyses" />
      <Resource name="market_swot_items" />
      {/* Marketing (own channels, content plans, pieces, weekly metrics) */}
      <Resource name="marketing_channels" />
      <Resource name="content_plans" />
      <Resource name="content_plan_pillars" />
      <Resource name="content_plan_phases" />
      <Resource name="content_plan_channels" />
      <Resource name="content_items" />
      <Resource name="content_metrics" />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncStoragePersister,
        dehydrateOptions: {
          // Picks the queries saved to localStorage for offline use: successful
          // ones (react-query's default rule, inlined because the persister
          // types its own copy of query-core), except those flagged with
          // `meta: { persist: false }`, like the global search, which caches one
          // query per typed term.
          shouldDehydrateQuery: (query) =>
            query.state.status === "success" && query.meta?.persist !== false,
        },
      }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes noLayout>
          <Route path={SignupPage.path} element={<SignupPage />} />
          <Route
            path={ConfirmationRequired.path}
            element={<ConfirmationRequired />}
          />
          <Route path={SetPasswordPage.path} element={<SetPasswordPage />} />
          <Route
            path={ForgotPasswordPage.path}
            element={<ForgotPasswordPage />}
          />
          <Route path={OAuthConsentPage.path} element={<OAuthConsentPage />} />
          <Route path={StylePresent.path} element={<StylePresent />} />
          <Route path={QuotePrint.path} element={<QuotePrint />} />
          <Route path={PublicSharePage.path} element={<PublicSharePage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path={ProfilePage.path} element={<ProfilePage />} />
          <Route path={MOBILE_CRM_SETTINGS_PATH} element={<SettingsPage />} />
          <Route path={ImportPage.path} element={<ImportPage />} />
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route path={ChangelogPage.path} element={<ChangelogPage />} />
          <Route
            path={CalendarPage.path}
            element={
              <div className="px-4">
                <CalendarPage />
              </div>
            }
          />
          {/* The calendar replaced the phone agenda (its Agenda view covers
              it): old /agenda links land there instead of a 404. */}
          <Route
            path="/agenda"
            element={<Navigate to={MOBILE_CALENDAR_PATH} replace />}
          />
          <Route path="/informes" element={<MobileReports />} />
          <Route path="/informes/:id" element={<MobileDealShow />} />
          {/* Messages module removed: old links land on the home page. */}
          <Route path="/mensajes" element={<Navigate to="/" replace />} />
          <Route path={MobileMorePage.path} element={<MobileMorePage />} />
          <Route path={MarketIntelPage.path} element={<MarketIntelPage />} />
          <Route path={CompetitorsPage.path} element={<CompetitorsPage />} />
          <Route path={MarketingPage.path} element={<MarketingPage />} />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          create={ContactCreateMobile}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource
          name="companies"
          list={CompanyListMobile}
          show={CompanyShow}
          create={CompanyCreateMobile}
          edit={CompanyEditMobile}
        />
        <Resource name="deals" {...deals} />
        <Resource name="tasks" list={MobileTasksList} />
        <Resource name="calendar_items" />
        {/* Same modules as desktop (see layout/mobileModules.ts) */}
        <Resource name="contact_notes" />
        <Resource name="deal_notes" />
        <Resource name="sales" {...sales} />
        <Resource name="tags" />
        <Resource name="team_tasks" {...teamTasks} />
        <Resource name="events" {...events} />
        <Resource name="team_task_comments" />
        <Resource name="time_entries" {...timeEntries} />
        <Resource name="deal_milestones" />
        <Resource name="deal_milestones_progress" />
        <Resource name="deals_delivery" />
        <Resource name="deals_time" />
        <Resource name="project_links" />
        <Resource name="share_links" />
        <Resource name="delivery_templates" {...deliveryTemplates} />
        <Resource name="styles" {...styles} />
        <Resource name="wiki_pages" {...wikiPages} />
        <Resource name="subscriptions" {...subscriptions} />
        <Resource name="quotes" {...quotes} />
        <Resource name="invoices" {...invoices} />
        <Resource name="gdpr_checklists" />
        {/* Market intelligence (reports and digests are read-only) */}
        <Resource name="market_daily_reports" />
        <Resource name="market_daily_items" />
        <Resource name="market_weekly_digests" />
        <Resource name="market_competitors" />
        <Resource name="market_competitor_updates" />
        <Resource name="market_ideas" />
        <Resource name="market_opportunities" />
        {/* Competencia (competitor intelligence, trends, SWOT) */}
        <Resource name="competitor_social_profiles" />
        <Resource name="competitor_social_snapshots" />
        <Resource name="competitor_marketing_activity" />
        <Resource name="competitor_products" />
        <Resource name="market_trends" />
        <Resource name="market_swot_analyses" />
        <Resource name="market_swot_items" />
        {/* Marketing (own channels, content plans, pieces, weekly metrics) */}
        <Resource name="marketing_channels" />
        <Resource name="content_plans" />
        <Resource name="content_plan_pillars" />
        <Resource name="content_plan_phases" />
        <Resource name="content_plan_channels" />
        <Resource name="content_items" />
        <Resource name="content_metrics" />
      </Admin>
    </PersistQueryClientProvider>
  );
};
